import {
  Button,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormLabelProps,
  HStack,
  Input,
  InputProps,
} from '@chakra-ui/react';
import { ChangeEventHandler, PropsWithoutRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues, RegisterOptions, UseFormSetValue } from 'react-hook-form';
import { onlyNumbers } from 'utils/number';
import { maskPixKey, unMaskPixKey } from 'utils/pix';
import FormLabelCustom from './FormLabel';

interface FormInputProps<T extends FieldValues = FieldValues> extends PropsWithoutRef<InputProps> {
  name: string;
  label: string;
  control: Control<T, object>;
  outerProps?: PropsWithoutRef<FormControlProps>;
  labelProps?: PropsWithoutRef<FormLabelProps>;
  registerOptions?: RegisterOptions;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  setValue: UseFormSetValue<T>;
  setPixTypeKey: (type: 'CPF' | 'PHONE' | null) => void;
}

function PixKeyInput<T extends FieldValues = FieldValues>({
  name,
  label,
  outerProps = {},
  labelProps = {},
  control,
  registerOptions = {},
  setValue,
  setPixTypeKey,
  ...props
}: FormInputProps<T>) {
  const [showTypePix, setShowTypePix] = useState(false);
  const [typePix, setTypePix] = useState<'CPF' | 'PHONE' | null>(null);

  return (
    <Controller
      name={name as FieldPath<T>}
      control={control}
      rules={registerOptions as RegisterOptions<T, FieldPath<T>>}
      defaultValue={'' as never}
      render={({ field, formState: { isSubmitting }, fieldState: { error } }) => (
        <FormControl
          {...outerProps}
          isInvalid={!!error}
        >
          <FormLabelCustom
            label={label}
            name={name}
            props={labelProps}
          />

          <Input
            {...field}
            {...props}
            id={name}
            isDisabled={isSubmitting}
            onChange={(v) => {
              /*
               * Se o valor anterior do campo for uma chave pix CPF/CNPJ ou telefone
               * Então removemos todos os caracteres especiais para não buggar a mascara
               */

              const unkasked = unMaskPixKey(field.value);
              console.log(v.target.value);
              const unkasked2 = maskPixKey(onlyNumbers(v.target.value));

              const startNumber = onlyNumbers(unkasked.masked).substring(0, 2);

              const isNumber = !isNaN(Number(startNumber));

              console.log(isNumber, startNumber, unkasked2.type);

              if (unkasked2.type === 'CPF' && isNumber && +startNumber > 10 && +startNumber < 60) {
                setShowTypePix(true);
              } else {
                setShowTypePix(false);
                setTypePix(null);
              }

              return field.onChange(
                maskPixKey(
                  [`CPF`, `CNPJ`, `PHONE`].includes(unkasked.type) ? onlyNumbers(v.target.value) : v.target.value,
                ).masked,
              );
            }}
          />
          {showTypePix && (
            <HStack mt={2}>
              <Button
                variant="outline"
                colorScheme={typePix === 'CPF' ? 'blue' : 'gray'}
                onClick={() => {
                  setTypePix('CPF');
                  setPixTypeKey('CPF');
                  setValue(name as FieldPath<T>, maskPixKey(onlyNumbers(field.value), 'CPF').masked as never);
                }}
              >
                CPF
              </Button>
              <Button
                variant="outline"
                colorScheme={typePix === 'PHONE' ? 'blue' : 'gray'}
                onClick={() => {
                  setTypePix('PHONE');
                  setPixTypeKey('PHONE');
                  setValue(name as FieldPath<T>, maskPixKey(onlyNumbers(field.value), 'PHONE').masked as never);
                }}
              >
                Telefone
              </Button>
            </HStack>
          )}

          <FormErrorMessage>{error && error.message}</FormErrorMessage>
        </FormControl>
      )}
    />
  );
}

export default PixKeyInput;
